import React, { useEffect, useId, useRef, useState } from 'react';
import styles from './FAQ.module.css';
import { useNavigation } from '../../../utils/redirectUtils';
import { trackPostHogEvent } from '../../../utils/posthogUtils';
import { startInterview } from '../../../utils/interviewFlow';

// Default programme FAQ, shared by the base landings and /semaglutide.
// Pages with their own questions (e.g. the CEO landing) pass `items`/`description`.
const DEFAULT_DESCRIPTION = "Everything you might want to know about compounded semaglutide, dosing, billing, and what's included in your program.";

const DEFAULT_ITEMS = [
    {
        question: 'Is compounded semaglutide FDA-approved?',
        answer: 'Compounded medications are not FDA-approved, not evaluated for safety or efficacy, and not equivalent to brand-name versions. They are prescribed when clinically appropriate by a licensed clinician.',
    },
    {
        question: 'How is dosing determined?',
        answer: 'Your clinician determines dosing and adjusts over time. If prescribed, clinician-directed dose adjustments are included within your program.',
    },
    {
        question: 'How does billing work?',
        answer: 'Subscription renews monthly unless canceled. Future charges are disclosed before purchase. Full refund if not prescribed; additional cases handled per policy.',
    },
    {
        question: 'How should I store the medication?',
        answer: 'Follow pharmacy instructions. Many compounded GLP-1 medications require refrigeration at 36–46°F (2–8°C).',
    },
    {
        question: 'Who prescribes and dispenses?',
        answer: 'TB2 facilitates care. Clinicians prescribe independently. State-licensed pharmacies dispense medications.',
    },
];

function FAQItem({ question, answer, isOpen, onToggle }) {
    const contentId = useId();
    const innerRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(0);

    useEffect(() => {
        const el = innerRef.current;
        if (!el) return;
        const measure = () => setMaxHeight(el.scrollHeight);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [answer]);

    return (
        <div className={`${styles.faqItem} ${isOpen ? styles.faqItemOpen : ''}`}>
            <button
                type="button"
                className={styles.faqSummary}
                onClick={onToggle}
                aria-expanded={isOpen}
                aria-controls={contentId}
            >
                <span className={styles.faqQuestion}>{question}</span>
                <span className={`${styles.faqIcon} ${isOpen ? styles.faqIconOpen : ''}`} aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </span>
            </button>
            <div
                id={contentId}
                className={styles.faqPanel}
                style={{ maxHeight: isOpen ? maxHeight : 0 }}
            >
                <div ref={innerRef} className={styles.faqBody}>
                    {answer}
                </div>
            </div>
        </div>
    );
}

function FAQ({ items = DEFAULT_ITEMS, description = DEFAULT_DESCRIPTION, ctaLabel = 'Get Started', onCtaClick }) {
    const navigate = useNavigation();
    const [openIndex, setOpenIndex] = useState(0);

    const handleGetStarted = () => {
        // Enroll before tracking, so trackExposure has registered the variant
        // on this person and the submit event below carries it.
        const flow = startInterview();

        trackPostHogEvent('landing_page_submitted', {
            selected: 'faq_get_started',
            to_path: '/interview',
            interview_flow: flow.id,
        });
        navigate(flow.entryPath);
    };

    const toggle = (i) => setOpenIndex(prev => prev === i ? -1 : i);

    return (
        <section className={styles.faqSection}>
            <div className={styles.faqGrid}>
                <div className={styles.faqSide}>
                    <h2>Questions, answered.</h2>
                    <p>{description}</p>
                    <button className={styles.faqCta} onClick={onCtaClick ?? handleGetStarted}>
                        {ctaLabel}
                    </button>
                </div>

                <div className={styles.faqList}>
                    {items.map((item, i) => (
                        <FAQItem
                            key={i}
                            question={item.question}
                            answer={item.answer}
                            isOpen={openIndex === i}
                            onToggle={() => toggle(i)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}

export default FAQ;
